// =====================================================================
// src/screens/v2/HelpFAQ.jsx
//
// V2 Help & FAQ screen.
//
// Searchable, categorized FAQ accordions with a "Still stuck?" contact
// card at the bottom.
//
// In Phase B, FAQ data may move to Remote Config or a CMS for dynamic
// updates without app deploy. For now, FAQs are hardcoded.
//
// Props:
//   onBack — return to settings/profile
// =====================================================================

import React, { useState, useMemo, useEffect } from 'react';
import {
  ArrowLeft, Search, X, ChevronDown, Clock, ShoppingBag,
  Shield, User, Sparkles, Mail
} from 'lucide-react';
import './HelpFAQ.css';

// ============================================================
// FAQ DATA
// ============================================================

const CATEGORIES = [
  { id: 'all',      label: 'All',              icon: Sparkles,    color: 'accent' },
  { id: 'started',  label: 'Getting Started',  icon: Clock,       color: 'accent' },
  { id: 'business', label: 'Business Connect', icon: ShoppingBag, color: 'gold' },
  { id: 'orders',   label: 'Orders & Payment', icon: ShoppingBag, color: 'success' },
  { id: 'privacy',  label: 'Privacy',          icon: Shield,      color: 'info' },
  { id: 'account',  label: 'Account',          icon: User,        color: 'ink' },
];

const FAQS = [
  // Getting Started
  { cat: 'started', q: 'My batch wasn\'t found in search',
    a: [
      'If no one from your batch has joined Aangan yet, your batch won\'t show up in search.',
      'Be the first member — tap "Create my own" from the join screen. You\'ll get a connect code to share on WhatsApp with old batchmates.'
    ],
    keywords: 'batch find search join create not found' },
  { cat: 'started', q: 'How long until my join request is approved?',
    a: [
      'Most admins review within a few hours. If 72 hours pass with no response, code-based provisional access is auto-removed.',
      'If you\'ve waited longer, ask the person who shared the link to nudge the admin, or email aangansupport@gmail.com.'
    ],
    keywords: 'approval admin pending request wait time' },
  { cat: 'started', q: 'Why can\'t I see posts yet?',
    a: [
      'You\'re probably awaiting admin approval. Until then, you can see the batch name and members but not posts.',
      'You can also browse Local Businesses while you wait — that doesn\'t require batch approval.'
    ],
    keywords: 'posts visible content awaiting approval pending' },
  { cat: 'started', q: 'How do I create a new batch?',
    a: [
      'Profile → Add another batch → Create my own. You\'ll be the first admin and get a shareable code/link.',
      'Share that link on WhatsApp groups, alumni networks, or directly with batchmates you remember.'
    ],
    keywords: 'create batch new school college admin' },

  // Business Connect
  { cat: 'business', q: 'What does it mean to "Connect" with a business?',
    a: [
      'Connecting tells the business you\'re interested. Their posts appear in your Connect List, and you can place orders when they\'re open.',
      'Phone and WhatsApp numbers only become visible after the business activates Aangan Connect.'
    ],
    keywords: 'connect business meaning subscribe follow' },
  { cat: 'business', q: 'Why can\'t I see the business\'s phone number?',
    a: [
      'Contact details are hidden until: (1) you connect with them, (2) they\'re verified by Aangan, AND (3) they activate Aangan Connect.',
      'This protects businesses from spam and ensures only genuine customers reach them.'
    ],
    keywords: 'phone whatsapp visible contact hidden gated' },
  { cat: 'business', q: 'How does a business get verified?',
    a: [
      'Businesses submit GST or shop registration documents. Our team verifies them within 2–3 working days.',
      'A gold checkmark next to the business name means verified.'
    ],
    keywords: 'verified business gold check badge approve' },
  { cat: 'business', q: 'What is "Aangan Connect activation"?',
    a: [
      'It\'s a separate activation a business does offline with our team — it unlocks features like direct phone/WhatsApp visibility to connected customers.',
      'Aangan doesn\'t charge any fee inside the app for activation. All activation is handled directly by our team.'
    ],
    keywords: 'aangan connect activation activate offline' },

  // Orders & Payment
  { cat: 'orders', q: 'How does payment work?',
    a: [
      'Aangan doesn\'t process payments. After the business confirms your order, they share payment details (UPI / cash / link) in chat.',
      'You pay them directly. Aangan only facilitates the conversation.'
    ],
    keywords: 'payment upi cash pay how works' },
  { cat: 'orders', q: 'What if my order is wrong or never arrives?',
    a: [
      'For any order issue (wrong items, late delivery, refund requests), chat directly with the business in-app.',
      'The business handles all order issues, refunds, and replacements directly.'
    ],
    keywords: 'wrong order refund missing late issue' },
  { cat: 'orders', q: 'Can I cancel an order?',
    a: [
      'You can cancel an order while it\'s in "Placed" status. Once the business marks it Confirmed or Preparing, you\'ll need to chat with them.',
      'Most businesses are flexible if you reach out quickly.'
    ],
    keywords: 'cancel order placed confirmed' },
  { cat: 'orders', q: 'Are there delivery charges?',
    a: [
      'Delivery charges (if any) are set by each business and shown in your cart before you place the order.',
      'Aangan doesn\'t add any platform fee. The total you see is what the business charges.'
    ],
    keywords: 'delivery fee charges cost shipping' },

  // Privacy
  { cat: 'privacy', q: 'Who can see my profile?',
    a: [
      'Your profile (name, photo, batch) is visible to other approved members of your shared batches and to businesses you\'ve connected with.',
      'Phone number is never shown publicly.'
    ],
    keywords: 'profile visible see who privacy' },
  { cat: 'privacy', q: 'How do I report someone?',
    a: [
      'Tap the 3-dot menu on any post or profile → Report. Choose a reason and submit. We review reports within 24 hours.',
      'Repeated violations result in account removal.'
    ],
    keywords: 'report spam abuse harassment user' },
  { cat: 'privacy', q: 'Does Aangan use my location?',
    a: [
      'No. Aangan never collects GPS or location data.',
      'Your city is taken from your profile (which you can edit anytime), and businesses are filtered by that city.'
    ],
    keywords: 'location gps privacy collect city' },
  { cat: 'privacy', q: 'How do I file a privacy complaint?',
    a: [
      'For DPDPA grievances, email aangangrievance@gmail.com. Our Grievance Officer responds within 7 working days.',
      'For general privacy questions, email aangansupport@gmail.com.'
    ],
    keywords: 'dpdpa grievance privacy data' },

  // Account
  { cat: 'account', q: 'How do I change my email?',
    a: [
      'For security, email changes go through our support team. Email aangansupport@gmail.com from your current registered email with your request.',
      'We\'ll verify and update within 1–2 working days.'
    ],
    keywords: 'change email update support' },
  { cat: 'account', q: 'How do I delete my account?',
    a: [
      'Profile → Settings → Delete account. You\'ll be asked to confirm.',
      'Deletion is permanent — your posts, connections, and order history can\'t be recovered. Allow up to 7 days for full deletion across our systems.'
    ],
    keywords: 'delete account remove' },
  { cat: 'account', q: 'I\'m getting too many notifications',
    a: [
      'Profile → Settings → Notifications. You can mute specific batches, posts, or businesses, or pause all notifications temporarily.',
      'You can also unfollow individual posts that aren\'t relevant to you.'
    ],
    keywords: 'notifications mute too many spam' },
];

// ============================================================
// COMPONENT
// ============================================================

export default function HelpFAQ({ onBack }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [openIds, setOpenIds] = useState(new Set());
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  // Filter FAQs by category + search
  const visibleFaqs = useMemo(() => {
    let list = FAQS;
    if (category !== 'all') {
      list = list.filter(f => f.cat === category);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(f =>
        f.q.toLowerCase().includes(q) ||
        f.a.join(' ').toLowerCase().includes(q) ||
        (f.keywords || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [category, searchQuery]);

  // Group by category for display
  const groupedFaqs = useMemo(() => {
    const groups = {};
    visibleFaqs.forEach(f => {
      if (!groups[f.cat]) groups[f.cat] = [];
      groups[f.cat].push(f);
    });
    return groups;
  }, [visibleFaqs]);

  // Counts for filter chips
  const counts = useMemo(() => {
    const c = { all: FAQS.length };
    CATEGORIES.forEach(cat => {
      if (cat.id !== 'all') c[cat.id] = FAQS.filter(f => f.cat === cat.id).length;
    });
    return c;
  }, []);

  function toggleFaq(idx, cat) {
    const id = `${cat}-${idx}`;
    setOpenIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="v2-help-faq">
      {/* Top bar */}
      <div className="v2hf-top-bar">
        <button className="v2hf-back-btn" onClick={onBack} aria-label="Back">
          <ArrowLeft size={20} />
        </button>
        <div className="v2hf-top-title">Help & FAQ</div>
      </div>

      {/* Search */}
      <div className="v2hf-search-section">
        <div className="v2hf-search-bar">
          <Search size={16} />
          <input
            type="text"
            className="v2hf-search-input"
            placeholder="Search help — e.g. order, payment, batch…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              className="v2hf-search-clear"
              onClick={() => setSearchQuery('')}
              aria-label="Clear"
            >
              <X size={12} />
            </button>
          )}
        </div>
      </div>

      {/* Filter chips */}
      <div className="v2hf-filter-bar">
        {CATEGORIES.map(cat => (
          <button
            key={cat.id}
            className={`v2hf-filter-chip ${category === cat.id ? 'active' : ''}`}
            onClick={() => setCategory(cat.id)}
          >
            {cat.label}
            <span className="v2hf-fc-count">{counts[cat.id]}</span>
          </button>
        ))}
      </div>

      {/* Scroll body */}
      <div className="v2hf-scroll-body">
        {visibleFaqs.length === 0 ? (
          <div className="v2hf-empty">
            <div className="v2hf-empty-icon">🔍</div>
            <div className="v2hf-empty-title">No results match "{searchQuery}"</div>
            <div className="v2hf-empty-sub">
              Try different words, or contact us below.
            </div>
          </div>
        ) : (
          CATEGORIES.filter(c => c.id !== 'all').map(cat => {
            const list = groupedFaqs[cat.id];
            if (!list || list.length === 0) return null;
            const Icon = cat.icon;
            return (
              <section key={cat.id} className="v2hf-section">
                <div className="v2hf-section-header">
                  <div className={`v2hf-section-icon v2hf-si-${cat.color}`}>
                    <Icon size={14} />
                  </div>
                  <div className="v2hf-section-title">{cat.label}</div>
                </div>
                <div className="v2hf-faq-list">
                  {list.map((f, idx) => {
                    const id = `${f.cat}-${FAQS.indexOf(f)}`;
                    const isOpen = openIds.has(id);
                    return (
                      <div
                        key={id}
                        className={`v2hf-faq-card ${isOpen ? 'open' : ''}`}
                      >
                        <button
                          className="v2hf-faq-summary"
                          onClick={() => toggleFaq(FAQS.indexOf(f), f.cat)}
                        >
                          <span>{f.q}</span>
                          <ChevronDown
                            size={14}
                            className="v2hf-chev"
                            style={{
                              transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
                              transition: 'transform 0.2s'
                            }}
                          />
                        </button>
                        {isOpen && (
                          <div className="v2hf-faq-answer">
                            {f.a.map((para, pIdx) => (
                              <p key={pIdx}>{para}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })
        )}

        {/* Still stuck card */}
        <div className="v2hf-contact-card">
          <div className="v2hf-contact-icon">
            <Mail size={20} />
          </div>
          <div className="v2hf-contact-body">
            <div className="v2hf-contact-title">Still stuck?</div>
            <div className="v2hf-contact-text">
              Email us — we usually reply within 1 working day.
            </div>
            <a
              className="v2hf-contact-link"
              href="mailto:aangansupport@gmail.com"
            >
              aangansupport@gmail.com →
            </a>
          </div>
        </div>
      </div>

      {toast && <div className="v2hf-toast">{toast}</div>}
    </div>
  );
}
